import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { slotsAPI, defaultAssignmentsAPI } from '../../services/api';
import type { TrainingType, TrainingSlot, RecurrencePattern } from '../../types';
import BookingsModal from './BookingsModal';
import DefaultAssignmentsModal from './DefaultAssignmentsModal';
import { format } from 'date-fns';

const EventsTab: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingSlot, setEditingSlot] = useState<TrainingSlot | null>(null);
  const [viewingBookings, setViewingBookings] = useState<{ id: string; title: string } | null>(null);
  const [viewingDefaultAssignments, setViewingDefaultAssignments] = useState<{ id: string; title: string; capacity: number } | null>(null);
  const queryClient = useQueryClient();

  const { data: slots = [] } = useQuery({
    queryKey: ['slots', 'all'],
    queryFn: () => slotsAPI.getSlots(undefined, true),
  });

  // Fetch default assignments for all slots to show counts
  const slotIds = useMemo(() => slots.map(s => s.id), [slots]);
  
  // Fetch default assignments count for each slot
  const defaultAssignmentsQueries = useQuery({
    queryKey: ['defaultAssignments', 'all', slotIds.join(',')],
    queryFn: async () => {
      const assignmentsMap = new Map<string, number>();
      await Promise.all(
        slotIds.map(async (slotId) => {
          try {
            const assignments = await defaultAssignmentsAPI.getSlotDefaultAssignments(slotId);
            assignmentsMap.set(slotId, assignments.length);
          } catch (error) {
            assignmentsMap.set(slotId, 0);
          }
        })
      );
      return assignmentsMap;
    },
    enabled: slotIds.length > 0,
  });

  const defaultAssignmentsCount = defaultAssignmentsQueries.data || new Map();

  const createMutation = useMutation({
    mutationFn: slotsAPI.createSlot,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setShowForm(false);
      // Show success message if multiple slots were created
      if (Array.isArray(data) && data.length > 1) {
        alert(`Successfully created ${data.length} recurring events!`);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => slotsAPI.updateSlot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setEditingSlot(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: slotsAPI.deleteSlot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slots'] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      type: formData.get('type') as TrainingType,
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      date: formData.get('date') as string,
      startTime: formData.get('startTime') as string,
      endTime: formData.get('endTime') as string,
      capacity: parseInt(formData.get('capacity') as string),
      recurrencePattern: (formData.get('recurrencePattern') as RecurrencePattern) || 'NONE',
      recurrenceEndDate: formData.get('recurrenceEndDate') as string || undefined,
      recurrenceCount: formData.get('recurrenceCount')
        ? parseInt(formData.get('recurrenceCount') as string)
        : undefined,
    };

    if (editingSlot) {
      updateMutation.mutate({ id: editingSlot.id, data });
    } else {
      createMutation.mutate(data as any);
    }
  };

  const handleEdit = (slot: TrainingSlot) => {
    setEditingSlot(slot);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSlot(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Training Events</h2>
        <button
          onClick={() => {
            if (showForm) {
              handleCancel();
            } else {
              setShowForm(true);
              setEditingSlot(null);
            }
          }}
          className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
        >
          {showForm ? 'Cancel' : 'Create New Event'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-xl font-semibold mb-4">
            {editingSlot ? 'Edit Event' : 'Create New Event'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  name="type"
                  required
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingSlot?.type || "SWIMMING"}
                >
                  <option value="SWIMMING">Swimming</option>
                  <option value="BIKING">Biking</option>
                  <option value="RUNNING">Running</option>
                  <option value="GYM">Gym</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  required
                  className="w-full border rounded px-3 py-2"
                  placeholder="Morning Swim"
                  defaultValue={editingSlot?.title || ""}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  required
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingSlot ? format(new Date(editingSlot.date), 'yyyy-MM-dd') : ""}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  name="startTime"
                  required
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingSlot?.startTime || ""}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  name="endTime"
                  required
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingSlot?.endTime || ""}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacity
                </label>
                <input
                  type="number"
                  name="capacity"
                  required
                  min="1"
                  defaultValue={editingSlot?.capacity || 10}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recurrence Pattern
                </label>
                <select
                  name="recurrencePattern"
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingSlot?.recurrencePattern || "NONE"}
                >
                  <option value="NONE">None (Single Event)</option>
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recurrence End Date (Optional)
                </label>
                <input
                  type="date"
                  name="recurrenceEndDate"
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingSlot?.recurrenceEndDate ? format(new Date(editingSlot.recurrenceEndDate), 'yyyy-MM-dd') : ""}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use occurrence count instead
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Occurrences (Optional)
                </label>
                <input
                  type="number"
                  name="recurrenceCount"
                  min="1"
                  max="365"
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., 10"
                  defaultValue={editingSlot?.recurrenceCount || ""}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use end date instead (max 365)
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                className="w-full border rounded px-3 py-2"
                rows={3}
                defaultValue={editingSlot?.description || ""}
              />
            </div>
            <button
              type="submit"
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
            >
              {editingSlot ? 'Update' : 'Create'} Event
            </button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Bookings
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {slots.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  No events found. Create your first event!
                </td>
              </tr>
            ) : (
              slots.map((slot) => (
                <tr key={slot.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(slot.date).toLocaleDateString()}
                    <br />
                    <span className="text-gray-500">
                      {slot.startTime} - {slot.endTime}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{slot.type}</td>
                  <td className="px-6 py-4 text-sm">{slot.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {slot.recurrencePattern && slot.recurrencePattern !== 'NONE' ? (
                      // For recurring events, show default assignments count instead of total bookings
                      <span>
                        <span className="font-medium">{defaultAssignmentsCount.get(slot.id) || 0}</span> defaults
                        <br />
                        <span className="text-xs text-gray-500">
                          Capacity: {slot.capacity}
                        </span>
                      </span>
                    ) : (
                      // For single events, show booking count
                      `${slot.bookingCount}/${slot.capacity}`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setViewingBookings({ id: slot.id, title: slot.title })}
                          className="text-primary-600 hover:text-primary-800 font-medium"
                        >
                          View Bookings ({slot.bookingCount})
                        </button>
                        <button
                          onClick={() => setViewingDefaultAssignments({ id: slot.id, title: slot.title, capacity: slot.capacity })}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          Default Assignments
                        </button>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => handleEdit(slot)}
                          className="border border-primary-600 text-primary-600 hover:bg-primary-50 px-3 py-1 rounded text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(slot.id)}
                          className="text-accent-600 hover:text-accent-700 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bookings Modal */}
      {viewingBookings && (
        <BookingsModal
          slotId={viewingBookings.id}
          slotTitle={viewingBookings.title}
          onClose={() => setViewingBookings(null)}
        />
      )}

      {/* Default Assignments Modal */}
      {viewingDefaultAssignments && (
        <DefaultAssignmentsModal
          slotId={viewingDefaultAssignments.id}
          slotTitle={viewingDefaultAssignments.title}
          slotCapacity={viewingDefaultAssignments.capacity}
          onClose={() => setViewingDefaultAssignments(null)}
        />
      )}
    </div>
  );
};

export default EventsTab;

